// =============================================================================
// Calendar Service - Unified abstraction for Google + Apple Calendar
// =============================================================================

import { APPLE_COLOR_PALETTE, GOOGLE_COLOR_PALETTE, COLOR_CATEGORY_MAP } from './constants/colors';

const WEB_TO_SHORT_COLOR = {
  'bg-blue-400': 'blue', 'bg-green-400': 'green', 'bg-yellow-400': 'yellow',
  'bg-purple-400': 'purple', 'bg-pink-400': 'pink', 'bg-gray-400': 'gray',
};
const SHORT_TO_WEB_COLOR = {
  blue: 'bg-blue-400', green: 'bg-green-400', yellow: 'bg-yellow-400',
  purple: 'bg-purple-400', pink: 'bg-pink-400', gray: 'bg-gray-400',
};

// --- Section A: Environment Detection ---

export function isCapacitor() {
  return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
}

// --- Section B: Apple Calendar (Capacitor-ready) ---

export async function requestAppleCalendarAccess() {
  if (!isCapacitor()) {
    return { granted: false, reason: 'browser' };
  }

  try {
    const { CapacitorCalendar } = await import('../shims/capacitor-calendar');
    const result = await CapacitorCalendar.requestPermission();
    if (result.granted) {
      return { granted: true };
    }
    return { granted: false, reason: 'denied' };
  } catch (err) {
    console.error('Apple Calendar permission error:', err);
    return { granted: false, reason: 'error', error: err.message };
  }
}

export async function fetchAppleCalendarEvents(startDate, endDate) {
  if (!isCapacitor()) {
    return [];
  }

  try {
    const { CapacitorCalendar } = await import('../shims/capacitor-calendar');
    const result = await CapacitorCalendar.listEvents({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
    return result.events || [];
  } catch (err) {
    console.error('Apple Calendar fetch error:', err);
    throw err;
  }
}

export function transformAppleEvents(capacitorEvents) {
  return capacitorEvents.map((event, index) => {
    const start = new Date(event.startDate || event.start);
    const isAllDay = event.allDay || false;
    const palette = APPLE_COLOR_PALETTE[index % APPLE_COLOR_PALETTE.length];

    let endTime = '';
    if (!isAllDay && (event.endDate || event.end)) {
      const end = new Date(event.endDate || event.end);
      endTime = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
    }

    return {
      id: `apple_${event.id}`,
      title: event.title || '(No title)',
      time: isAllDay
        ? 'All Day'
        : start.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }),
      endTime,
      month: start.getMonth() + 1,
      day: start.getDate(),
      year: start.getFullYear(),
      color: palette.color,
      dotColor: palette.dotColor,
      alert: true,
      tags: [],
      needsPrep: false,
      source: 'apple',
      location: event.location || '',
    };
  });
}

// --- Section C: Google Calendar ---

const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/calendar';

let tokenClient = null;

function loadGisScript() {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.accounts) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
}

export async function initGoogleAuth(clientId, onSuccess, onError) {
  try {
    await loadGisScript();
    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: GOOGLE_SCOPES,
      callback: (tokenResponse) => {
        if (tokenResponse.error) {
          onError(tokenResponse);
        } else {
          onSuccess(tokenResponse.access_token);
        }
      },
    });
  } catch (err) {
    console.error('Google auth init error:', err);
    if (onError) onError({ error: err.message });
  }
}

export function requestGoogleAccess() {
  if (tokenClient) {
    tokenClient.requestAccessToken();
  }
}

export async function fetchGoogleEvents(accessToken, timeMin, timeMax) {
  const url = new URL('https://www.googleapis.com/calendar/v3/calendars/primary/events');
  url.searchParams.set('timeMin', timeMin);
  url.searchParams.set('timeMax', timeMax);
  url.searchParams.set('singleEvents', 'true');
  url.searchParams.set('orderBy', 'startTime');
  url.searchParams.set('maxResults', '250');

  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!res.ok) {
    const error = new Error(`Google Calendar API error: ${res.status}`);
    error.status = res.status;
    throw error;
  }

  const data = await res.json();
  return data.items || [];
}

export async function createGoogleEvent(accessToken, eventData) {
  const url = 'https://www.googleapis.com/calendar/v3/calendars/primary/events';

  const body = {
    summary: eventData.title,
    start: {},
    end: {},
  };

  if (eventData.startTime) {
    const startDt = new Date(`${eventData.date}T${eventData.startTime}:00`);
    const endDt = eventData.endTime
      ? new Date(`${eventData.date}T${eventData.endTime}:00`)
      : new Date(startDt.getTime() + 60 * 60 * 1000);
    body.start.dateTime = startDt.toISOString();
    body.end.dateTime = endDt.toISOString();
  } else {
    body.start.date = eventData.date;
    // Google requires end date to be exclusive for all-day events
    const nextDay = new Date(eventData.date);
    nextDay.setDate(nextDay.getDate() + 1);
    body.end.date = nextDay.toISOString().split('T')[0];
  }

  if (eventData.location) {
    body.location = eventData.location;
  }

  const privateProps = {};
  if (eventData.prepTime != null) privateProps.appdhd_prepTime = String(eventData.prepTime);
  if (eventData.colorKey) privateProps.appdhd_colorKey = WEB_TO_SHORT_COLOR[eventData.colorKey] || eventData.colorKey;
  if (eventData.tags) privateProps.appdhd_tags = JSON.stringify(eventData.tags);
  if (Object.keys(privateProps).length > 0) {
    body.extendedProperties = { private: privateProps };
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const error = new Error(`Google Calendar API error: ${res.status}`);
    error.status = res.status;
    throw error;
  }

  return await res.json();
}

export async function updateGoogleEvent(accessToken, googleEventId, eventData) {
  const url = `https://www.googleapis.com/calendar/v3/calendars/primary/events/${googleEventId}`;

  const body = {
    summary: eventData.title,
    start: {},
    end: {},
  };

  if (eventData.startTime) {
    const startDt = new Date(`${eventData.date}T${eventData.startTime}:00`);
    const endDt = eventData.endTime
      ? new Date(`${eventData.date}T${eventData.endTime}:00`)
      : new Date(startDt.getTime() + 60 * 60 * 1000);
    body.start.dateTime = startDt.toISOString();
    body.end.dateTime = endDt.toISOString();
  } else {
    body.start.date = eventData.date;
    const nextDay = new Date(eventData.date);
    nextDay.setDate(nextDay.getDate() + 1);
    body.end.date = nextDay.toISOString().split('T')[0];
  }

  if (eventData.location) {
    body.location = eventData.location;
  }

  const privateProps = {};
  if (eventData.prepTime != null) privateProps.appdhd_prepTime = String(eventData.prepTime);
  if (eventData.colorKey) privateProps.appdhd_colorKey = WEB_TO_SHORT_COLOR[eventData.colorKey] || eventData.colorKey;
  if (eventData.tags) privateProps.appdhd_tags = JSON.stringify(eventData.tags);
  if (Object.keys(privateProps).length > 0) {
    body.extendedProperties = { private: privateProps };
  }

  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const error = new Error(`Google Calendar API error: ${res.status}`);
    error.status = res.status;
    throw error;
  }

  return await res.json();
}

export function transformGoogleEvents(googleEvents) {
  return googleEvents.map((event, index) => {
    const isAllDay = !event.start.dateTime;
    const start = new Date(event.start.dateTime || event.start.date);
    const defaultPalette = GOOGLE_COLOR_PALETTE[index % GOOGLE_COLOR_PALETTE.length];

    let endTime = '';
    if (!isAllDay && event.end?.dateTime) {
      const end = new Date(event.end.dateTime);
      endTime = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
    }

    // Restore app-only fields from extended properties
    const ext = event.extendedProperties?.private || {};
    const storedColorKey = ext.appdhd_colorKey || null;
    const webColorKey = storedColorKey ? (SHORT_TO_WEB_COLOR[storedColorKey] || storedColorKey) : null;
    const resolved = webColorKey && COLOR_CATEGORY_MAP[webColorKey]
      ? COLOR_CATEGORY_MAP[webColorKey]
      : { color: defaultPalette.color, dotColor: defaultPalette.dotColor };

    let tags = [];
    if (ext.appdhd_tags) {
      try { tags = JSON.parse(ext.appdhd_tags); } catch (_) {}
    }

    const prepTime = ext.appdhd_prepTime ? Number(ext.appdhd_prepTime) : 0;

    return {
      id: `google_${event.id}`,
      title: event.summary || '(No title)',
      time: isAllDay
        ? 'All Day'
        : start.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }),
      endTime,
      month: start.getMonth() + 1,
      day: start.getDate(),
      year: start.getFullYear(),
      color: resolved.color,
      dotColor: resolved.dotColor,
      colorKey: webColorKey || null,
      alert: true,
      tags,
      needsPrep: prepTime > 0,
      prepTime,
      source: 'google',
      googleEventId: event.id,
      location: event.location || '',
    };
  });
}
